/**
 * Where bulky belongings actually go on a pallet.
 *
 * This module exists because a scalar cannot answer the question. Charging a
 * loose-stacked item `volume / 0.65` is an honest placeholder that says "it
 * costs more than its own volume" without ever saying how much more, which is a
 * fact about that item against this interior and not a constant. Marcus's
 * ruling was explicit: *"there is no hardcoded how much of the pallet be used,
 * it all depends"*. Depending on something means computing it, so bulky items
 * are placed here and the free space that falls out is what boxed goods fill.
 *
 * **Deliberately knows nothing about `MeasuredItem`, the estimator, or Zod.** It
 * takes dimension triples and an interior, and answers with pallets and the
 * spaces left empty on them. `pallet-spec.ts` owns the domain meaning; keeping
 * the geometry importable on its own is what keeps it testable without building
 * an item fixture, and what stops the two source files importing each other.
 *
 * ## The model
 *
 * A pallet is a stack of **layers**. A layer's height is set by whatever opens
 * it, and its floor is filled with **rows** running along the interior's length.
 * Identical units stack in a column to the layer's ceiling. Shapes are placed
 * largest-first, and a pallet closes when nothing left can ride under the
 * height that remains.
 *
 * ## What it promises
 *
 * **Every arrangement it reports physically exists.** Rows never overlap (a row
 * claims width for its whole length, and length is consumed left to right),
 * layers never overlap (each claims its own height), columns never pass their
 * layer's ceiling, and nothing is placed in an orientation whose footprint
 * exceeds the floor. The error only ever runs one way, and that direction is
 * chosen: over-quoting sells a customer a pallet they half fill, while
 * under-quoting advises them too few, so they take delivery of too few empties
 * and run out mid-pack — which costs another order and another visit before the
 * pickup can happen.
 *
 * **The same promise covers `freeSpaces`.** They are the empty rectangular
 * boxes left over, reported with their dimensions rather than as one volume —
 * because whoever tops them up has to know whether what it is adding physically
 * fits. Returning one volume per pallet and pouring boxed goods into it would
 * credit a 44 x 18 x 40 wardrobe's 18.5"-wide strip with 34,427 cubic inches,
 * so a 40 x 30 x 28 carton — 33,600, and 28" across its smallest face — would
 * be quoted into it. Two independent sweeps put that at 15% and 17% of mixed
 * loads. A number cannot say "not through that door".
 *
 * ## What it does not promise
 *
 * **It is not the optimum.** Optimal 3D bin packing is NP-hard, and these are
 * dimensions a model guessed from a photograph — false precision on top of them
 * buys nothing. It is *achievable*, which is measured in
 * `pallet-placement.test.ts` rather than asserted here.
 *
 * **`freeSpaces` is not exhaustive either.** A pallet has empty room this does
 * not report: the gap beside an item in a row deeper than the item, and
 * whatever a later shape left in a row it had already claimed. Those go
 * unreported rather than unfilled, which costs a quote air and never a pallet
 * the customer needed.
 */

/** The three interior axes, in inches. */
export interface PlacementInterior {
  lengthInches: number
  widthInches: number
  heightInches: number
}

/**
 * So many units of one bulky shape.
 *
 * `dims` is the item's three measurements in any order — this module sorts and
 * orients them itself, because which measurement the model called "length" says
 * nothing about how the thing rides.
 */
export interface BulkyGroup {
  dims: readonly [number, number, number]
  units: number
}

/**
 * An empty rectangular box left on a pallet, and how many pallets have one
 * exactly like it.
 *
 * Dimensions rather than a volume, and that is the whole contract: whoever
 * fills these has to be able to ask whether a thing FITS, which a volume cannot
 * answer. See the module header for what a volume let through.
 */
export interface FreeSpace {
  lengthInches: number
  widthInches: number
  heightInches: number
  count: number
}

export interface BulkyPlacement {
  /** Pallets the bulky items occupy on their own. */
  pallets: number
  /** Empty boxes left on those pallets. Never overlapping. */
  freeSpaces: FreeSpace[]
}

/**
 * How an item rides: which measurement points up, and its footprint.
 *
 * `runLong`/`runShort` are the footprint's two sides, longer first. Both ways
 * round are tried at placement time, so this describes the footprint rather
 * than fixing its rotation; the only thing fixed here is which measurement is
 * the height.
 */
interface Orientation {
  height: number
  runLong: number
  runShort: number
}

/**
 * Every way this shape can ride — the one fitting the most of it on an empty
 * pallet first, then the rest flattest-first.
 *
 * Scored rather than guessed, because the obvious rule is wrong in a case that
 * is not exotic: a 22.2 x 24.2 x 28.0 chest lies flattest on its 28 x 24.2 face
 * and then exactly one fits the floor, where standing it on its 28 x 22.2 face
 * puts two 22.2" runs down the 44.5" length with a tenth of an inch to spare.
 * "Lay everything as flat as it goes" quotes that load at double.
 *
 * The alternatives earn their place twice over: a shape joining a layer
 * somebody else opened is limited by that layer's ceiling, and a shape that
 * cannot open a layer in its best orientation may still lie down in the height
 * left. Flattest-first is the order both want.
 *
 * Empty only for a shape that cannot ride at all, which the caller has already
 * excluded — `assessFit` compares the item's dimensions and the interior's both
 * sorted largest-first, so a fitting item always has the orientation
 * (height = middle dimension, footprint = largest x smallest) available here.
 */
function rideableOrientations(
  dims: readonly [number, number, number],
  interior: PlacementInterior,
): Orientation[] {
  const [largest, middle, smallest] = [...dims].sort((a, b) => b - a)
  const candidates: Orientation[] = [
    { height: smallest, runLong: largest, runShort: middle },
    { height: middle, runLong: largest, runShort: smallest },
    { height: largest, runLong: middle, runShort: smallest },
  ]

  const rideable: { orientation: Orientation; units: number }[] = []
  for (const candidate of candidates) {
    if (candidate.height > interior.heightInches) continue
    const units =
      unitsThatFit(interior.heightInches, candidate.height) *
      Math.max(
        unitsThatFit(interior.lengthInches, candidate.runLong) *
          unitsThatFit(interior.widthInches, candidate.runShort),
        unitsThatFit(interior.lengthInches, candidate.runShort) *
          unitsThatFit(interior.widthInches, candidate.runLong),
      )
    if (units <= 0) continue
    // Two candidates can be value-identical — a cube has one distinct shape, a
    // square-faced box two — and retrying one costs a whole placement pass for
    // nothing. Cubes and square footprints are common.
    const duplicate = rideable.some(
      (kept) =>
        kept.orientation.height === candidate.height &&
        kept.orientation.runLong === candidate.runLong &&
        kept.orientation.runShort === candidate.runShort,
    )
    if (duplicate) continue
    rideable.push({ orientation: candidate, units })
  }
  if (rideable.length === 0) return []

  let best = rideable[0]
  for (const option of rideable) {
    if (!best) break
    // More per pallet wins; a tie goes to the lower height, which leaves the
    // most ceiling for a second layer of something else.
    if (
      option.units > best.units ||
      (option.units === best.units &&
        option.orientation.height < best.orientation.height)
    ) {
      best = option
    }
  }
  const chosen = best
  const rest = rideable
    .filter((option) => option !== chosen)
    .sort((a, b) => a.orientation.height - b.orientation.height)
  return [
    ...(chosen ? [chosen.orientation] : []),
    ...rest.map((option) => option.orientation),
  ]
}

/** A shape, oriented every way it can ride, with the count still to place. */
interface PlaceableShape {
  orientations: Orientation[]
  units: number
}

/**
 * A row of the current layer: it has claimed `depth` of the floor's width for
 * its whole length, and `lengthLeft` of that length is still empty.
 */
interface Row {
  depth: number
  lengthLeft: number
}

/**
 * How many rows of a layer the placement scan looks back over.
 *
 * The same *reason* as `packUnits`' `FIRST_FIT_LOOKBACK` — not the same number,
 * and "the same bound" would be wrong: that one
 * is 64. A thin item opens a row per unit, so an unbounded scan is quadratic in
 * a quantity the customer types. Missing a row can only open another one, never
 * put two items in the same space, so the bound keeps the arrangement
 * achievable and can only push the count up.
 */
const ROW_LOOKBACK = 32

/**
 * How many times a pallet is simulated before the remainder is finished the
 * crude way.
 *
 * Pallets are built one at a time and then **repeated in bulk**: a simulated
 * pallet is a recipe, applied as many times as the remaining counts allow, so a
 * line of 999 wardrobes costs one simulation rather than 999. Each repetition
 * drives at least one shape below its own per-pallet usage, so the recipe
 * changes a bounded number of times per distinct shape.
 *
 * **Measured at the real ceiling, not a convenient one.** The estimator accepts
 * `MAX_ESTIMATOR_ITEMS` (300) lines at `MAX_ESTIMATED_ITEM_QUANTITY` (999)
 * each; 150 random loads that size peaked at 397 recipes. "Far inside this
 * bound" against a cap of 512 is a 29% margin, measured on a 100-line fixture
 * — the wrong ceiling and an overstated conclusion drawn from it.
 *
 * The bound exists because "far inside on every load I measured" is not a
 * proof. The fallback it guards is deliberately crude and deliberately safe:
 * see `palletsAlone`.
 */
const MAX_PALLET_RECIPES = 4096

/**
 * Place bulky units against the interior, and report the empty boxes left over.
 *
 * A `dims` triple carrying a non-finite or non-positive measurement, or one no
 * orientation can ride, is **skipped** rather than charged — the caller has
 * already partitioned those out as `unfittable`, and silently inventing a
 * pallet for a shape that cannot ride would contradict it.
 */
export function placeBulkyUnits(
  groups: readonly BulkyGroup[],
  interior: PlacementInterior,
): BulkyPlacement {
  // Collapse identical shapes before anything is placed, so the answer depends
  // only on the multiset of shapes and not on how the customer's list was
  // grouped. The key is the SORTED triple, because which measurement the model
  // labelled "length" is not a fact about the object.
  //
  // **The split spelling is the one to defend, and the usual reason given for
  // that is false.** Comments here and in `pallet-spec.ts` have long said photo
  // extraction emits one line per object; the prompt says the opposite —
  // "Merge obvious duplicates into one row with a higher quantity"
  // (`estimator.service.ts`), which is why `quantity` exists at all. Five lines
  // of one chair arrive when the model does NOT obey that, when the customer
  // typed the list, or when objects are alike enough to read as one and
  // different enough not to be. Rarer than claimed, and none the less real.
  const unitsByShape = new Map<string, PlaceableShape>()
  for (const group of groups) {
    if (!Number.isFinite(group.units) || group.units <= 0) continue
    if (group.dims.some((d) => !Number.isFinite(d) || d <= 0)) continue
    const orientations = rideableOrientations(group.dims, interior)
    if (orientations.length === 0) continue
    const key = [...group.dims].sort((a, b) => b - a).join('x')
    const existing = unitsByShape.get(key)
    if (existing) {
      existing.units += group.units
      continue
    }
    unitsByShape.set(key, { orientations, units: group.units })
  }

  // Tallest first, by the orientation each shape prefers. A layer's height is
  // set by whatever opens it, so starting with the tallest thing left is what
  // keeps short items filling a tall layer's floor rather than a tall item
  // wasting a short layer's ceiling.
  const shapes = [...unitsByShape.values()].sort(
    (a, b) =>
      (b.orientations[0]?.height ?? 0) - (a.orientations[0]?.height ?? 0),
  )

  let pallets = 0
  const freeSpaces: FreeSpace[] = []
  let recipes = 0
  while (shapes.some((shape) => shape.units > 0)) {
    if (recipes >= MAX_PALLET_RECIPES) {
      pallets += palletsAlone(shapes, interior)
      // No free spaces: every pallet the fallback opens is quoted as though its
      // shape were the only thing on it, and reporting room on top of that
      // would be reporting room this branch never established.
      return { pallets, freeSpaces }
    }
    recipes += 1

    const { used, voids } = fillOnePallet(shapes, interior)
    if (used.size === 0) break

    // The recipe, repeated. `times` is what the scarcest shape in it allows, so
    // every one of those pallets is the arrangement just simulated.
    let times = Number.POSITIVE_INFINITY
    for (const [index, count] of used) {
      const shape = shapes[index]
      if (!shape) continue
      times = Math.min(times, Math.floor(shape.units / count))
    }
    if (!Number.isFinite(times) || times < 1) times = 1
    for (const [index, count] of used) {
      const shape = shapes[index]
      if (shape) shape.units -= count * times
    }

    pallets += times
    for (const space of voids) {
      if (space.count <= 0) continue
      freeSpaces.push({ ...space, count: space.count * times })
    }
  }

  return { pallets, freeSpaces }
}

/**
 * How many units of a shape fit inside an empty space, in any orientation.
 *
 * Exported because the caller decides what may ride in the room this module
 * reports, and it has to ask that question the same way the placement answered
 * it. A second spelling of "does this fit" is exactly how the seam this
 * replaced went wrong.
 */
export function unitsOfShapeIn(
  space: FreeSpace,
  dims: readonly [number, number, number],
): number {
  if (dims.some((d) => !Number.isFinite(d) || d <= 0)) return 0
  const [largest, middle, smallest] = [...dims].sort((a, b) => b - a)
  const arrangements: [number, number, number][] = [
    [largest, middle, smallest],
    [largest, smallest, middle],
    [middle, largest, smallest],
    [middle, smallest, largest],
    [smallest, largest, middle],
    [smallest, middle, largest],
  ]
  let best = 0
  for (const [alongLength, alongWidth, alongHeight] of arrangements) {
    const fit =
      unitsThatFit(space.lengthInches, alongLength) *
      unitsThatFit(space.widthInches, alongWidth) *
      unitsThatFit(space.heightInches, alongHeight)
    if (fit > best) best = fit
  }
  return best
}

/**
 * Fill one pallet from what is left, and report what it consumed and what it
 * left empty.
 *
 * Keyed by the shape's index in `shapes` rather than by the shape itself, so
 * the caller can decrement counts without this function mutating them — a
 * recipe is simulated before anyone knows how many times it will be applied.
 */
function fillOnePallet(
  shapes: readonly PlaceableShape[],
  interior: PlacementInterior,
): { used: Map<number, number>; voids: FreeSpace[] } {
  const used = new Map<number, number>()
  const voids: FreeSpace[] = []
  const remaining = shapes.map((shape) => shape.units)
  let heightLeft = interior.heightInches

  while (true) {
    const layerHeight = openingHeight(shapes, remaining, heightLeft)
    if (layerHeight === null) break

    const empty = fillOneLayer(shapes, remaining, used, layerHeight, interior)
    for (const space of empty) voids.push(space)
    heightLeft -= layerHeight
  }

  if (heightLeft > 0) {
    voids.push({
      lengthInches: interior.lengthInches,
      widthInches: interior.widthInches,
      heightInches: heightLeft,
      count: 1,
    })
  }
  return { used, voids }
}

/**
 * The height of the next layer, or `null` when nothing else can ride.
 *
 * **Two passes, and the second is a bug fix rather than a refinement.** The
 * first prefers the tallest shape still unplaced, in the orientation it packs
 * best in — the arrangement a person loading a pallet builds. If no shape can
 * open a layer that way, the second asks whether anything can LIE DOWN in the
 * height left.
 *
 * Without that second pass a pallet closed with usable ceiling above it while a
 * shape that would have fitted flat was pushed onto a new one — and because a
 * shorter item could open the layer that let it in, **adding an item made the
 * quote cheaper**. The estimator recomputes when a customer prunes their list,
 * so a customer saw that as removing something and watching the price go up.
 */
function openingHeight(
  shapes: readonly PlaceableShape[],
  remaining: readonly number[],
  heightLeft: number,
): number | null {
  let best: number | null = null
  for (const [index, shape] of shapes.entries()) {
    if ((remaining[index] ?? 0) <= 0) continue
    const preferred = shape.orientations[0]
    if (preferred && preferred.height <= heightLeft) {
      if (best === null || preferred.height > best) best = preferred.height
    }
  }
  if (best !== null) return best

  for (const [index, shape] of shapes.entries()) {
    if ((remaining[index] ?? 0) <= 0) continue
    for (const orientation of shape.orientations) {
      if (orientation.height > heightLeft) continue
      if (best === null || orientation.height > best) best = orientation.height
    }
  }
  return best
}

/**
 * Fill one layer's floor with every shape short enough to ride in it, and
 * report the empty boxes it leaves.
 *
 * Placement is by whole counts, never unit by unit: a row that takes six of a
 * shape takes them in one step. That is what keeps this affordable on the
 * customer's phone — `estimatePallets` runs in the render body, so every prune
 * and every tap of a quantity stepper pays this cost synchronously.
 */
function fillOneLayer(
  shapes: readonly PlaceableShape[],
  remaining: number[],
  used: Map<number, number>,
  layerHeight: number,
  interior: PlacementInterior,
): FreeSpace[] {
  const rows: Row[] = []
  const headroom: FreeSpace[] = []
  let widthLeft = interior.widthInches

  for (const [index, shape] of shapes.entries()) {
    let unplaced = remaining[index] ?? 0
    if (unplaced <= 0) continue

    for (const orientation of shape.orientations) {
      if (unplaced <= 0) break
      if (orientation.height > layerHeight) continue
      const { runLong, runShort } = orientation
      const before = unplaced

      // How many of this shape stand on one footprint before the layer's
      // ceiling. A layer is a slab as tall as whatever opened it, so anything
      // shorter has headroom above it — and identical objects stack, which is
      // how anyone loads a pallet. It cannot over-credit: the column is as tall
      // as the layer it sits in and no taller.
      const stack = Math.max(unitsThatFit(layerHeight, orientation.height), 1)

      // Top up rows already open, most recent first — rows are opened for
      // progressively shorter shapes, so the newest are the ones with a depth
      // this shape can still ride under.
      const from = Math.max(0, rows.length - ROW_LOOKBACK)
      for (let i = rows.length - 1; i >= from && unplaced > 0; i--) {
        const row = rows[i]
        if (!row || row.lengthLeft <= 0) continue
        const run = bestRunForDepth(
          runLong,
          runShort,
          row.depth,
          row.lengthLeft,
        )
        if (run === null) continue
        const fit = unitsThatFit(row.lengthLeft, run) * stack
        if (fit <= 0) continue
        const placed = Math.min(unplaced, fit)
        row.lengthLeft -= Math.ceil(placed / stack) * run
        unplaced -= placed
      }

      // Then open new rows, in bulk, while the floor still has width for one.
      if (unplaced > 0) {
        const opened = bestRowOrientation(
          runLong,
          runShort,
          widthLeft,
          interior.lengthInches,
        )
        if (opened !== null) {
          const perRow = unitsThatFit(interior.lengthInches, opened.run) * stack
          if (perRow > 0) {
            const rowsWanted = Math.ceil(unplaced / perRow)
            const rowsPossible = Math.floor(widthLeft / opened.depth + 1e-9)
            const rowsOpened = Math.min(rowsWanted, rowsPossible)
            for (let r = 0; r < rowsOpened; r++) {
              const placed = Math.min(unplaced, perRow)
              rows.push({
                depth: opened.depth,
                lengthLeft:
                  interior.lengthInches -
                  Math.ceil(placed / stack) * opened.run,
              })
              widthLeft -= opened.depth
              unplaced -= placed
            }
          }
        }
      }

      // The empty box above each column this orientation placed. Real space: a
      // column is as tall as its stack, the layer is taller, and the difference
      // sits over that column's own footprint.
      const placedNow = before - unplaced
      const spare = layerHeight - stack * orientation.height
      if (placedNow > 0 && spare > 0) {
        headroom.push({
          lengthInches: runLong,
          widthInches: runShort,
          heightInches: spare,
          count: Math.ceil(placedNow / stack),
        })
      }
    }

    const placedHere = (remaining[index] ?? 0) - unplaced
    if (placedHere > 0) {
      remaining[index] = unplaced
      used.set(index, (used.get(index) ?? 0) + placedHere)
    }
  }

  const empty: FreeSpace[] = [...headroom]
  for (const row of rows) {
    if (row.lengthLeft > 0) {
      empty.push({
        lengthInches: row.lengthLeft,
        widthInches: row.depth,
        heightInches: layerHeight,
        count: 1,
      })
    }
  }
  if (widthLeft > 0) {
    empty.push({
      lengthInches: interior.lengthInches,
      widthInches: widthLeft,
      heightInches: layerHeight,
      count: 1,
    })
  }
  return empty
}

/**
 * The footprint side to run along the length, given a row's depth.
 *
 * A footprint may be laid either way round, so both are tried and the one
 * fitting more units into the room left wins. `null` means neither side is
 * shallow enough for this row.
 */
function bestRunForDepth(
  runLong: number,
  runShort: number,
  depth: number,
  lengthLeft: number,
): number | null {
  const epsilon = 1e-9
  const canRunLong = runShort <= depth + epsilon
  const canRunShort = runLong <= depth + epsilon
  if (!canRunLong && !canRunShort) return null
  if (!canRunShort) return runLong
  if (!canRunLong) return runShort
  return unitsThatFit(lengthLeft, runShort) > unitsThatFit(lengthLeft, runLong)
    ? runShort
    : runLong
}

/**
 * Which way round to lay a footprint when opening a fresh row.
 *
 * Chosen by units per inch of width claimed, because width is the scarce axis
 * once a row is opened: a row claims its depth from the floor for good, whether
 * or not its length is ever filled.
 */
function bestRowOrientation(
  runLong: number,
  runShort: number,
  widthLeft: number,
  length: number,
): { run: number; depth: number } | null {
  const options = [
    { run: runLong, depth: runShort },
    { run: runShort, depth: runLong },
  ]
  let best: { run: number; depth: number } | null = null
  let bestDensity = 0
  for (const option of options) {
    if (option.depth > widthLeft + 1e-9) continue
    if (option.run > length + 1e-9) continue
    const density = unitsThatFit(length, option.run) / option.depth
    if (best === null || density > bestDensity) {
      best = option
      bestDensity = density
    }
  }
  return best
}

/**
 * The crude finish: every shape on pallets of its own.
 *
 * Only reachable past `MAX_PALLET_RECIPES`. It cannot under-count — a shape
 * given pallets to itself is placed by a grid and shares with nothing — and it
 * is cheap, which is the whole point of having it.
 *
 * The grid tries every rideable orientation and the footprint both ways round,
 * matching `unitsOfShapeIn` and the orientation scorer. Taking one orientation
 * one way round silently collapses to a unit per pallet for any shape whose
 * better arrangement is the other: safe, and much too dear.
 */
function palletsAlone(
  shapes: readonly PlaceableShape[],
  interior: PlacementInterior,
): number {
  let pallets = 0
  for (const shape of shapes) {
    if (shape.units <= 0) continue
    let perPallet = 0
    for (const orientation of shape.orientations) {
      const { height, runLong, runShort } = orientation
      const layers = unitsThatFit(interior.heightInches, height)
      const perLayer = Math.max(
        unitsThatFit(interior.lengthInches, runLong) *
          unitsThatFit(interior.widthInches, runShort),
        unitsThatFit(interior.lengthInches, runShort) *
          unitsThatFit(interior.widthInches, runLong),
      )
      perPallet = Math.max(perPallet, layers * perLayer)
    }
    pallets += Math.ceil(shape.units / Math.max(perPallet, 1))
  }
  return pallets
}

/**
 * How many lengths of `size` fit in `room`, tolerating float error.
 *
 * **The single declaration** — `pallet-spec.ts` imports this rather than
 * keeping its own. Two copies existed briefly and had already diverged on the
 * zero guard below, which is the drift a "same reason, same epsilon" comment
 * cannot prevent.
 *
 * The tolerance is on the dimensionless ratio, making it relative: two items
 * that exactly fill a run divide to 1.9999999999999998 and floor to one, which
 * sells a customer a row they do not need. At 1e-9 it sits far below any
 * measurement a photograph or a customer could produce.
 *
 * **A size small enough to overflow the ratio returns a finite count**, never
 * `Infinity`. A dimension below about 2.5e-307 is a legal `z.number()` and
 * `assessFit` accepts it; left infinite it propagated into
 * `Math.ceil(n / perRow) === 0`, so a layer placed nothing, the pallet never
 * filled, and the render hung the customer's whole estimate with no way back.
 */
export function unitsThatFit(room: number, size: number): number {
  if (!(size > 0)) return 0
  const fit = Math.floor(room / size + 1e-9)
  return Number.isFinite(fit) ? fit : Number.MAX_SAFE_INTEGER
}
